import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  BuildingIcon,
  BellIcon,
  ShieldIcon,
  PaletteIcon,
  Loader2Icon,
  ChevronRightIcon,
  LayersIcon,
  DownloadIcon,
  MailIcon,
  TagIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAppSettings, type JobCompletionPhotoPolicy } from '../hooks/useAppSettings'
import { isPwaDisplayMode } from '../hooks/useAppShellMode'
import { usePermissions } from '../hooks/usePermissions'
import { DivisionsSettings } from '../components/settings/DivisionsSettings'
import { LabelSettings } from '../components/settings/LabelSettings'
import { MailboxSettings } from '../components/settings/MailboxSettings'
import { LanguageToggle } from '../components/LanguageToggle'
import {
  isDesktopNotificationPreferenceEnabled,
  isDesktopNotificationSupported,
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
  setDesktopNotificationPreference,
} from '../lib/desktopNotifications'
import {
  MobilePage,
  MobilePageHeader,
  MobileListGroup,
  MobileGroupRow,
  MobileSection,
  mobile,
} from '../components/mobile'

interface CompanySettings {
  company_name: string
  company_email: string
  company_phone: string
  company_address: string
  company_city: string
  company_state: string
  company_zip: string
}

export function Settings() {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canManageMailboxes = can('communication.inbox_admin')
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const { photoPolicy, setPhotoPolicy, loading: photoPolicyLoading, garbageBagBoxCosts, setGarbageBagBoxCosts } =
    useAppSettings()
  const [bagClearBox, setBagClearBox] = useState('')
  const [bagBlackBox, setBagBlackBox] = useState('')
  const [savingBagCosts, setSavingBagCosts] = useState(false)
  const [settings, setSettings] = useState<CompanySettings>({
    company_name: 'RBC Field Management',
    company_email: '',
    company_phone: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zip: '',
  })
  const [desktopNotifyEnabled, setDesktopNotifyEnabled] = useState(() =>
    isDesktopNotificationPreferenceEnabled()
  )
  const [desktopNotifyBusy, setDesktopNotifyBusy] = useState(false)
  const desktopNotifySupported = isDesktopNotificationSupported()

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'general')
  }, [searchParams])

  useEffect(() => {
    setBagClearBox(String(garbageBagBoxCosts.clearBoxOf100 ?? 0))
    setBagBlackBox(String(garbageBagBoxCosts.blackBoxOf100 ?? 0))
  }, [garbageBagBoxCosts.clearBoxOf100, garbageBagBoxCosts.blackBoxOf100])

  const handlePhotoPolicyChange = async (policy: JobCompletionPhotoPolicy) => {
    const result = await setPhotoPolicy(policy)
    if (!result.ok) {
      setMessage({ type: 'error', text: result.error || 'Failed to save photo policy' })
      return
    }
    setMessage({ type: 'success', text: 'Job completion photo policy updated.' })
  }

  const handleSaveBagCosts = async () => {
    setSavingBagCosts(true)
    const result = await setGarbageBagBoxCosts({
      clearBoxOf100: parseFloat(bagClearBox) || 0,
      blackBoxOf100: parseFloat(bagBlackBox) || 0,
    })
    setSavingBagCosts(false)
    if (!result.ok) {
      setMessage({ type: 'error', text: result.error || 'Failed to save bag costs' })
      return
    }
    setMessage({ type: 'success', text: 'Garbage bag box costs saved.' })
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .limit(1)
        .single()

      if (!error && data) {
        setSettings({
          company_name: data.name || data.company_name || 'RBC Field Management',
          company_email: data.email || data.company_email || '',
          company_phone: data.phone || data.company_phone || '',
          company_address: data.address || '',
          company_city: data.city || '',
          company_state: data.state || '',
          company_zip: data.zip || '',
        })
      }
    } catch {
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const { data: existing } = await supabase.from('companies').select('id').limit(1).single()

      if (existing) {
        const { error } = await supabase
          .from('companies')
          .update({
            name: settings.company_name,
            email: settings.company_email,
            phone: settings.company_phone,
            address: settings.company_address,
            city: settings.company_city,
            state: settings.company_state,
            zip: settings.company_zip,
          })
          .eq('id', existing.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('companies').insert({
          name: settings.company_name,
          email: settings.company_email,
          phone: settings.company_phone,
          address: settings.company_address,
          city: settings.company_city,
          state: settings.company_state,
          zip: settings.company_zip,
        })
        if (error) throw error
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' })
    } catch (err: any) {
      console.error('Error saving settings:', err)
      setMessage({ type: 'error', text: 'Failed to save settings: ' + (err.message || 'Unknown error') })
    } finally {
      setSaving(false)
      setTimeout(() => setMessage(null), 5000)
    }
  }

  const tabs = [
    { id: 'general', label: 'General', icon: BuildingIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'security', label: 'Security', icon: ShieldIcon },
    { id: 'appearance', label: 'Appearance', icon: PaletteIcon },
    { id: 'divisions', label: 'Divisions', icon: LayersIcon },
    ...(canManageMailboxes ? [{ id: 'mailboxes', label: 'Mailboxes', icon: MailIcon }] : []),
    ...(canManageMailboxes ? [{ id: 'labels', label: 'Labels', icon: TagIcon }] : []),
  ]

  const quickLinks = [
    { label: 'Directory', description: 'Company contacts and directory', path: '/directory' },
    { label: 'Service Catalog', description: 'Manage service items and categories', path: '/service-catalog' },
    { label: 'Combination Rules', description: 'Which service categories combine into one visit', path: '/combination-rules' },
    { label: 'Visit Group Templates', description: 'Reusable recurring schedule templates for contracts', path: '/visit-group-templates' },
    { label: 'Billing Milestone Templates', description: 'Progress billing presets for contracts and proposals', path: '/billing-milestone-templates' },
    { label: 'Checklist Templates', description: 'Reusable job checklists by service type', path: '/checklist-templates' },
    { label: 'System Logs', description: 'Recurring job run history and manual top-ups', path: '/system-logs' },
    { label: 'City Records Sync', description: 'Nightly 311/HPD/ECB/DSNY/PLUTO sync history and manual run', path: '/city-records-sync-logs' },
    { label: 'Team', description: 'People, logins, permissions, and crews', path: '/settings/team' },
    { label: 'QR Stickers', description: 'Generate and print sticker batches for field scan-to-assign', path: '/settings/qr-stickers' },
  ]

  const selectTab = (tabId: string) => {
    setActiveTab(tabId)
    setSearchParams(tabId === 'general' ? {} : { tab: tabId })
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Settings"
        subtitle={<span className="hidden sm:inline">Manage your application settings and preferences</span>}
      />

      {message && (
        <div
          className={`p-4 rounded-xl border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Mobile tab chips */}
      <div className="lg:hidden flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const active = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={`inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded-xl text-sm font-semibold whitespace-nowrap border transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
        {/* Sidebar — desktop */}
        <div className="hidden lg:block lg:col-span-1 space-y-4">
          <div className={`${mobile.surface} overflow-hidden`}>
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 min-h-[48px] text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 border-l-2 border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <MobileListGroup label="Quick links">
            {quickLinks.map((link) => (
              <MobileGroupRow key={link.path} onClick={() => navigate(link.path)}>
                <div className="flex items-center justify-between gap-3 w-full">
                  <div className="text-left min-w-0">
                    <p className="font-medium text-gray-900">{link.label}</p>
                    <p className="text-xs text-gray-500 truncate">{link.description}</p>
                  </div>
                  <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </div>
              </MobileGroupRow>
            ))}
          </MobileListGroup>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3 space-y-4">
          <MobileSection>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {activeTab === 'general' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className={mobile.label}>Company Name</label>
                        <input
                          type="text"
                          value={settings.company_name}
                          onChange={(e) =>
                            setSettings({ ...settings, company_name: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>Email</label>
                        <input
                          type="email"
                          value={settings.company_email}
                          onChange={(e) =>
                            setSettings({ ...settings, company_email: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>Phone</label>
                        <input
                          type="tel"
                          value={settings.company_phone}
                          onChange={(e) =>
                            setSettings({ ...settings, company_phone: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={mobile.label}>Address</label>
                        <input
                          type="text"
                          value={settings.company_address}
                          onChange={(e) =>
                            setSettings({ ...settings, company_address: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div>
                        <label className={mobile.label}>City</label>
                        <input
                          type="text"
                          value={settings.company_city}
                          onChange={(e) =>
                            setSettings({ ...settings, company_city: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={mobile.label}>State</label>
                          <input
                            type="text"
                            value={settings.company_state}
                            onChange={(e) =>
                              setSettings({ ...settings, company_state: e.target.value })
                            }
                            className={mobile.field}
                          />
                        </div>
                        <div>
                          <label className={mobile.label}>ZIP Code</label>
                          <input
                            type="text"
                            value={settings.company_zip}
                            onChange={(e) =>
                              setSettings({ ...settings, company_zip: e.target.value })
                            }
                            className={mobile.field}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">Job Completion</h2>
                      <p className="text-sm text-gray-600 mb-4">
                        Require photos before a job can be marked completed. Camera-only is for
                        field / mobile capture (no attach-from-files).
                      </p>
                      <select
                        value={photoPolicy}
                        disabled={photoPolicyLoading}
                        onChange={(e) =>
                          handlePhotoPolicyChange(e.target.value as JobCompletionPhotoPolicy)
                        }
                        className={`${mobile.field} max-w-md`}
                      >
                        <option value="off">Off — photos optional</option>
                        <option value="required">Required — any photo (camera or attach)</option>
                        <option value="camera_required">
                          Camera only — must be taken live (no attach button)
                        </option>
                      </select>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">Garbage bag costs</h2>
                      <p className="text-sm text-gray-600 mb-4">
                        Cost of a box of 100 bags. Job completion multiplies bag counts by unit cost
                        (box ÷ 100) and snapshots that COGS for P&amp;L and job costing.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                        <div>
                          <label className={mobile.label}>Black bags — box of 100 ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={bagBlackBox}
                            disabled={photoPolicyLoading || savingBagCosts}
                            onChange={(e) => setBagBlackBox(e.target.value)}
                            className={mobile.field}
                          />
                        </div>
                        <div>
                          <label className={mobile.label}>Clear bags — box of 100 ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={bagClearBox}
                            disabled={photoPolicyLoading || savingBagCosts}
                            onChange={(e) => setBagClearBox(e.target.value)}
                            className={mobile.field}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={savingBagCosts}
                        onClick={handleSaveBagCosts}
                        className={`${mobile.primaryBtn} mt-4`}
                      >
                        {savingBagCosts ? 'Saving…' : 'Save bag costs'}
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'notifications' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Notification Preferences
                    </h2>
                    <div className="space-y-1">
                      {[
                        { label: 'Email notifications for new leads', key: 'email_leads' },
                        { label: 'Email notifications for job completion', key: 'email_jobs' },
                        { label: 'Email notifications for proposal approvals', key: 'email_proposals' },
                        { label: 'Daily summary report', key: 'daily_summary' },
                        { label: 'Weekly performance report', key: 'weekly_report' },
                      ].map((item) => (
                        <div
                          key={item.key}
                          className="flex items-center justify-between py-3.5 min-h-[52px] border-b border-gray-100 last:border-0"
                        >
                          <span className="text-sm text-gray-700 pr-3">{item.label}</span>
                          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                            <input type="checkbox" defaultChecked className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                          </label>
                        </div>
                      ))}
                      <div className="flex items-center justify-between py-3.5 min-h-[52px] border-b border-gray-100">
                        <div className="pr-3 min-w-0">
                          <span className="text-sm text-gray-700 block">
                            Desktop notifications for messages
                          </span>
                          <span className="text-xs text-gray-500">
                            {!desktopNotifySupported
                              ? 'Not supported in this browser'
                              : getDesktopNotificationPermission() === 'denied'
                                ? 'Permission blocked in browser settings'
                                : 'Alerts for inbound customer messages and team chat'}
                          </span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={desktopNotifyEnabled}
                            disabled={
                              !desktopNotifySupported ||
                              desktopNotifyBusy ||
                              getDesktopNotificationPermission() === 'denied'
                            }
                            onChange={async (e) => {
                              const next = e.target.checked
                              setDesktopNotifyBusy(true)
                              try {
                                if (next) {
                                  const perm = await requestDesktopNotificationPermission()
                                  const ok = perm === 'granted'
                                  setDesktopNotifyEnabled(ok)
                                  if (!ok) setDesktopNotificationPreference(false)
                                } else {
                                  setDesktopNotificationPreference(false)
                                  setDesktopNotifyEnabled(false)
                                }
                              } finally {
                                setDesktopNotifyBusy(false)
                              }
                            }}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50" />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'security' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h2>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Change Password</h3>
                        <div className="space-y-3 max-w-md">
                          <input
                            type="password"
                            placeholder="Current password"
                            className={mobile.field}
                          />
                          <input
                            type="password"
                            placeholder="New password"
                            className={mobile.field}
                          />
                          <input
                            type="password"
                            placeholder="Confirm new password"
                            className={mobile.field}
                          />
                          <button type="button" className={mobile.primaryBtn}>
                            Update Password
                          </button>
                        </div>
                      </div>
                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          Two-Factor Authentication
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">
                          Add an extra layer of security to your account.
                        </p>
                        <button type="button" className={mobile.secondaryBtn}>
                          Enable 2FA
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'appearance' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Appearance Settings
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <LanguageToggle />
                        <p className="text-xs text-gray-500 mt-2">
                          Field screens (My Day, punch, jobs, clock) follow this setting.
                          Office screens stay in English until later.
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Theme</h3>
                        <div className="flex flex-wrap gap-2">
                          {['Light', 'Dark', 'System'].map((theme) => (
                            <button
                              key={theme}
                              type="button"
                              className={`min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-colors ${
                                theme === 'Light'
                                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {theme}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          Sidebar Default State
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {['Expanded', 'Collapsed'].map((state) => (
                            <button
                              key={state}
                              type="button"
                              className={`min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-colors ${
                                state === 'Expanded'
                                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {state}
                            </button>
                          ))}
                        </div>
                      </div>
                      {!isPwaDisplayMode() && (
                        <button
                          type="button"
                          onClick={() => navigate('/install')}
                          className={`${mobile.secondaryBtn} w-full sm:w-auto text-sm`}
                        >
                          <DownloadIcon className="w-4 h-4" />
                          Install app on phone
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'divisions' && <DivisionsSettings />}

                {activeTab === 'mailboxes' && canManageMailboxes && <MailboxSettings />}

                {activeTab === 'labels' && canManageMailboxes && <LabelSettings />}

                {activeTab !== 'divisions' && activeTab !== 'mailboxes' && activeTab !== 'labels' && (
                  <div className="mt-6 pt-4 border-t border-gray-200 flex justify-stretch sm:justify-end">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className={`${mobile.primaryBtn} w-full sm:w-auto`}
                    >
                      {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                )}
              </>
            )}
          </MobileSection>

          {/* Quick links — mobile */}
          <div className="lg:hidden">
            <MobileListGroup label="Quick links">
              {quickLinks.map((link) => (
                <MobileGroupRow key={link.path} onClick={() => navigate(link.path)}>
                  <div className="flex items-center justify-between gap-3 w-full">
                    <div className="text-left min-w-0">
                      <p className="font-medium text-gray-900">{link.label}</p>
                      <p className="text-xs text-gray-500 truncate">{link.description}</p>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </div>
                </MobileGroupRow>
              ))}
            </MobileListGroup>
          </div>
        </div>
      </div>
    </MobilePage>
  )
}
